import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ChefHat, Printer, Monitor, RefreshCw } from "lucide-react";
import KitchenScreen from "@/pages/kitchen/KitchenScreen";
import PrinterFeed from "@/pages/kitchen/PrinterFeed";

/**
 * Kitchen orchestrator.
 *
 * Each restaurant is configured (by admin) with one of:
 *   - "screen"  → live Kanban board only
 *   - "printer" → printable ticket queue only
 *   - "both"    → tabs for either view
 *
 * The page fetches the selected restaurant's config and renders the right
 * view(s). When no restaurant is selected yet, we default to Screen so the
 * kitchen never sees an empty page.
 */
export default function KitchenPage() {
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurantId, setSelectedRestaurantId] = useState("");
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("screen");

    // Bootstrap: list of restaurants
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/restaurants");
                if (!active) return;
                setRestaurants(data || []);
                if (data?.length) setSelectedRestaurantId((prev) => prev || data[0].id);
            } catch (error) {
                console.warn("[kitchen] bootstrap failed", error?.message);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const fetchConfig = useCallback(async () => {
        if (!selectedRestaurantId) return;
        try {
            const { data } = await api.get(`/kitchen/config?restaurant_id=${selectedRestaurantId}`);
            setConfig(data);
            // Pick a sensible default view based on config
            if (data.kitchen_output === "printer") setView("printer");
            else setView("screen");
        } catch (error) {
            console.warn("[kitchen] config fetch failed", error?.message);
        }
    }, [selectedRestaurantId]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    const mode = config?.kitchen_output || "screen";

    return (
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
            <Header
                restaurants={restaurants}
                selectedRestaurantId={selectedRestaurantId}
                onSelectRestaurant={setSelectedRestaurantId}
                mode={mode}
                onRefresh={fetchConfig}
            />

            {mode === "screen" && <KitchenScreen restaurantId={selectedRestaurantId} />}
            {mode === "printer" && <PrinterFeed restaurantId={selectedRestaurantId} />}
            {mode === "both" && (
                <Tabs value={view} onValueChange={setView} data-testid="kitchen-mode-tabs">
                    <TabsList className="mb-4">
                        <TabsTrigger value="screen" data-testid="kitchen-mode-screen">
                            <Monitor className="h-3.5 w-3.5 mr-1" /> Screen
                        </TabsTrigger>
                        <TabsTrigger value="printer" data-testid="kitchen-mode-printer">
                            <Printer className="h-3.5 w-3.5 mr-1" /> Printer
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="screen">
                        <KitchenScreen restaurantId={selectedRestaurantId} />
                    </TabsContent>
                    <TabsContent value="printer">
                        <PrinterFeed restaurantId={selectedRestaurantId} />
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
}

function modeLabel(mode) {
    if (mode === "printer") return "Printer queue";
    if (mode === "both") return "Screen + Printer";
    return "Live screen";
}

function modeIcon(mode) {
    if (mode === "printer") return Printer;
    return Monitor;
}

function Header({ restaurants, selectedRestaurantId, onSelectRestaurant, mode, onRefresh }) {
    const ModeIcon = modeIcon(mode);
    return (
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
                <h1 className="font-display text-3xl inline-flex items-center gap-2">
                    <ChefHat className="h-7 w-7" /> Kitchen
                </h1>
                <p className="text-sm text-muted-foreground inline-flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="inline-flex items-center gap-1" data-testid="kitchen-mode-badge">
                        <ModeIcon className="h-3 w-3" /> {modeLabel(mode)}
                    </Badge>
                    <span>configured by admin per restaurant.</span>
                </p>
            </div>
            <div className="flex items-center gap-2">
                <Select value={selectedRestaurantId} onValueChange={onSelectRestaurant}>
                    <SelectTrigger className="w-56" data-testid="kitchen-restaurant-select">
                        <SelectValue placeholder="Restaurant" />
                    </SelectTrigger>
                    <SelectContent>
                        {restaurants.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                                {r.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={onRefresh} data-testid="kitchen-refresh-button">
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
}
